import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../logo2.png';
import SidebarNav from "./SidebarNav";

const Payroll = () => {
    const [employees, setEmployees] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [selectedBankAccount, setSelectedBankAccount] = useState('');
    const [amount, setAmount] = useState('');
    const [taxPercentage, setTaxPercentage] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchEmployees();
        fetchBankAccounts();
    }, []);

    const fetchEmployees = async () => {
        const { data, error } = await supabase.from('employees').select('*');
        if (!error) {
            setEmployees(data);
        }
    };

    const fetchBankAccounts = async () => {
        const { data, error } = await supabase.from('bank_accounts').select('*');
        if (!error) {
            setBankAccounts(data);
        }
    };

    const handlePaySalary = async (e) => {
        e.preventDefault();

        // 输入验证
        if (!selectedEmployee || !selectedBankAccount || !amount || !taxPercentage) {
            setMessage('请填写所有必填项');
            return;
        }

        // 转换数值
        const salaryAmount = parseFloat(amount);
        const taxRate = parseFloat(taxPercentage);

        if (isNaN(salaryAmount) || isNaN(taxRate)) {
            setMessage('请输入有效的金额和税率');
            return;
        }

        // 计算税额和净工资
        const taxAmount = (salaryAmount * taxRate) / 100;
        const netSalary = salaryAmount - taxAmount;

        // 获取选择的员工和银行账户
        const employeeName = employees.find(emp => emp.id === selectedEmployee)?.name;
        const bankAccount = bankAccounts.find(acc => acc.id === selectedBankAccount);

        // 显示确认框，确认用户输入的信息
        const confirmation = window.confirm(
            `请确认以下信息:\n\n` +
            `员工姓名: ${employeeName}\n` +
            `银行账户: ${bankAccount?.bank_name} - ${bankAccount?.account_number}\n` +
            `工资总额: ${salaryAmount.toFixed(2)}\n` +
            `税率: ${taxRate}%\n` +
            `税额: ${taxAmount.toFixed(2)}\n` +
            `员工净工资: ${netSalary.toFixed(2)}\n` +
            `公司总支出: ${salaryAmount.toFixed(2)}`
        );

        // 如果用户取消确认，不执行后续操作
        if (!confirmation) {
            return;
        }

        // 获取银行账户余额
        const { data: bankData, error: bankError } = await supabase
            .from('bank_accounts')
            .select('balance')
            .eq('id', selectedBankAccount)
            .single();

        if (bankError || !bankData) {
            setMessage('获取银行账户余额时出错');
            return;
        }

        const currentBalance = bankData.balance;

        // 检查是否有足够的资金
        if (currentBalance < salaryAmount) {
            setMessage('银行账户资金不足');
            return;
        }

        // 扣除工资金额
        const newBalance = currentBalance - salaryAmount;

        // 添加交易记录（两条记录：员工工资和税款）
        const { error: transactionError } = await supabase.from('transactions').insert([
            {
                type: '工资',
                transaction_type: '运营费用',
                amount: netSalary,
                tax_percentage: taxRate,
                tax_amount: taxAmount,
                description: `支付给 ${employeeName} 的净工资`,
                bank_account_id: selectedBankAccount,
                date: new Date(),
            }
        ]);

        if (transactionError) {
            console.error('Transaction Error:', transactionError);
            setMessage(`处理付款时出错: ${transactionError.message}`);
            return;
        }

        // 更新银行账户余额
        const { error: updateError } = await supabase
            .from('bank_accounts')
            .update({ balance: newBalance })
            .eq('id', selectedBankAccount);

        if (updateError) {
            setMessage('更新银行账户余额时出错');
            return;
        }

        setMessage(`已支付 ${netSalary.toFixed(2)} 元给 ${employeeName}，税额为 ${taxAmount.toFixed(2)} 元。新的银行账户余额为 ${newBalance.toFixed(2)} 元。`);
        setAmount('');
        setTaxPercentage('');
        setSelectedEmployee('');
        setSelectedBankAccount('');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SidebarNav />

            {/* Main Content */}
            <div className="lg:pl-64">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <img
                                src={logo}
                                alt="Kindbridge Investments"
                                className="h-12 w-auto"
                            />
                        </div>
                        <h1 className="mt-6 text-2xl font-semibold text-gray-900">
                            工资管理
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            请填写以下表单记录员工工资支付
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                        <form onSubmit={handlePaySalary} className="p-6 space-y-6">
                            {/* Employee Selection */}
                            <div>
                                <label htmlFor="employee-select" className="block text-sm font-medium text-gray-700">
                                    选择员工<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    id="employee-select"
                                    value={selectedEmployee}
                                    onChange={(e) => setSelectedEmployee(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                >
                                    <option value="">选择员工</option>
                                    {employees.map((employee) => (
                                        <option key={employee.id} value={employee.id}>
                                            {employee.name} ({employee.email})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Bank Account Selection */}
                            <div>
                                <label htmlFor="bank-select" className="block text-sm font-medium text-gray-700">
                                    选择银行账户<span className="text-red-500 ml-1">*</span>
                                </label>
                                <select
                                    id="bank-select"
                                    value={selectedBankAccount}
                                    onChange={(e) => setSelectedBankAccount(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    required
                                >
                                    <option value="">选择银行账户</option>
                                    {bankAccounts.map((account) => (
                                        <option key={account.id} value={account.id}>
                                            {account.bank_name} - {account.account_number} (余额: {account.balance})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Amount and Tax Rate */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                                        工资总额<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="amount"
                                            type="number"
                                            placeholder="0.00"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="tax-percentage" className="block text-sm font-medium text-gray-700">
                                        税率<span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="tax-percentage"
                                            type="number"
                                            placeholder="0.00, 无税填0"
                                            value={taxPercentage}
                                            onChange={(e) => setTaxPercentage(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                    记录工资
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`mt-4 p-4 rounded-md ${
                            message.includes('成功')
                                ? 'bg-green-50 text-green-800'
                                : 'bg-red-50 text-red-800'
                        }`}>
                            <p className="text-sm">{message}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Payroll;
